/**
 * Thin wrapper over recharts' LineChart. Give it `data`, the `xKey` for the
 * category axis, and a `series` list describing which numeric keys to plot.
 * Renders responsively; falls back to an empty message when there's no data.
 */
import type { ReactElement } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as ReLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface ChartSeries {
  key: string;
  label: string;
  color?: string;
}

export interface LineChartProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  series: ChartSeries[];
  height?: number;
  emptyMessage?: string;
  className?: string;
}

/** Default colour cycle (brand blue + complementary hues). */
const DEFAULT_COLORS = ['#2563eb', '#16a34a', '#dc2626', '#f59e0b', '#7c3aed', '#0891b2'];

/** Responsive multi-series line chart. */
export default function LineChart({
  data,
  xKey,
  series,
  height = 300,
  emptyMessage = 'Không có dữ liệu để hiển thị.',
  className = '',
}: LineChartProps): ReactElement {
  if (!data || data.length === 0) {
    return (
      <div
        className={`flex items-center justify-center text-sm text-gray-500 ${className}`.trim()}
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className={className} style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ReLineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="#9ca3af" />
          <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" width={64} />
          <Tooltip />
          <Legend />
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </ReLineChart>
      </ResponsiveContainer>
    </div>
  );
}
